import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, Shield, Share2, Bell, FileText, Smartphone, 
  CheckCircle, ArrowRight, Syringe, FolderOpen, Users,
  Lock, Cloud, Sparkles, Star, ChevronRight, Play
} from 'lucide-react';
import { Button } from '../ui/button';

// Custom hook for scroll animations
const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold]);

  return [ref, isVisible];
};

const CarnetSanteSection = () => {
  const navigate = useNavigate();
  const isLoggedIn = localStorage.getItem('patientToken') || localStorage.getItem('vetToken');

  // Animation refs
  const [headerRef, headerVisible] = useScrollAnimation(0.2);
  const [cardRef, cardVisible] = useScrollAnimation(0.15);
  const [flowRef, flowVisible] = useScrollAnimation(0.2);
  const [showcaseRef, showcaseVisible] = useScrollAnimation(0.15);
  const [stepsRef, stepsVisible] = useScrollAnimation(0.2);
  const [benefitsRef, benefitsVisible] = useScrollAnimation(0.15);
  const [testimonialsRef, testimonialsVisible] = useScrollAnimation(0.2);
  const [ctaRef, ctaVisible] = useScrollAnimation(0.3);

  // Auto-rotate feature showcase
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % featureShowcase.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleCTA = () => {
    if (isLoggedIn) {
      // Utilisateur connecté -> Espace patient (section animaux)
      navigate('/espace-patient');
    } else {
      // Non connecté -> Page inscription/connexion
      navigate('/espace-patient?action=register');
    }
  };

  const ownerBenefits = [
    {
      icon: FolderOpen,
      title: "Documents centralisés",
      description: "Tous les documents médicaux de votre animal au même endroit"
    },
    {
      icon: Syringe,
      title: "Rappels vaccins automatiques",
      description: "Ne manquez plus jamais un rappel de vaccination"
    },
    {
      icon: Share2,
      title: "Partage sécurisé",
      description: "Partagez l'historique avec n'importe quel vétérinaire"
    },
    {
      icon: Smartphone,
      title: "Accessible partout",
      description: "Consultez le carnet depuis votre téléphone ou ordinateur"
    }
  ];

  const vetBenefits = [
    {
      icon: FileText,
      title: "Historique complet",
      description: "Accédez à l'historique médical complet de l'animal"
    },
    {
      icon: Users,
      title: "Continuité des soins",
      description: "Coordination facilitée avec les autres praticiens"
    },
    {
      icon: Bell,
      title: "Suivi patient",
      description: "Notifications des animaux partagés avec vous"
    }
  ];

  const steps = [
    { number: "1", title: "Créez votre compte", description: "Inscription gratuite en 2 minutes" },
    { number: "2", title: "Ajoutez votre animal", description: "Photo, informations, identification" },
    { number: "3", title: "Enrichissez le carnet", description: "Vaccins, traitements, documents" },
    { number: "4", title: "Partagez avec votre véto", description: "Invitez votre vétérinaire en 1 clic" }
  ];

  // Testimonials data - Orientés confiance et valeur pratique
  const testimonials = [
    {
      name: "Sophie M.",
      role: "Propriétaire",
      animalName: "Milo",
      animalEmoji: "🐕",
      avatar: "👩",
      text: "Grâce au carnet santé vetoRDV, je ne perds plus aucune ordonnance et mon vétérinaire a accès à tout l'historique de mon chien.",
      rating: 5
    },
    {
      name: "Laurent D.",
      role: "Propriétaire",
      animalName: "Caramel",
      animalEmoji: "🐱",
      avatar: "👨",
      text: "Les rappels de vaccins automatiques sont vraiment pratiques. Je n'oublie plus les échéances importantes.",
      rating: 5
    },
    {
      name: "Dr. Claire B.",
      role: "Vétérinaire",
      animalEmoji: "👨‍⚕️",
      avatar: "👩‍⚕️",
      text: "L'accès au carnet médical partagé me fait gagner du temps et améliore le suivi des animaux.",
      rating: 5
    }
  ];

  // Feature showcase data
  const featureShowcase = [
    {
      id: 'timeline',
      title: 'Timeline médicale',
      description: 'Visualisez tout l\'historique de santé de votre animal sur une frise chronologique interactive.',
      icon: '📅',
      color: 'from-blue-500 to-cyan-500',
      features: ['Vaccinations', 'Traitements', 'Consultations', 'Chirurgies']
    },
    {
      id: 'vaccins',
      title: 'Rappels vaccins',
      description: 'Recevez des alertes automatiques avant chaque rappel de vaccination.',
      icon: '💉',
      color: 'from-purple-500 to-pink-500',
      features: ['Alertes email', 'Notifications push', 'Calendrier intégré', 'Multi-animaux']
    },
    {
      id: 'documents',
      title: 'Gestion documents',
      description: 'Stockez ordonnances, analyses et radiographies en toute sécurité.',
      icon: '📄',
      color: 'from-orange-500 to-red-500',
      features: ['100 Mo stockage', 'PDF & Images', 'Téléchargement', 'Partage véto']
    },
    {
      id: 'partage',
      title: 'Partage vétérinaire',
      description: 'Donnez accès à n\'importe quel vétérinaire en quelques clics.',
      icon: '🤝',
      color: 'from-green-500 to-emerald-500',
      features: ['Invitation email', 'Droits lecture/écriture', 'Révocation simple', 'RGPD conforme']
    }
  ];

  const [activeFeature, setActiveFeature] = useState(0);

  return (
    <section className="relative py-16 md:py-24 overflow-hidden">
      {/* Animation styles */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        
        .animate-fadeInLeft {
          animation: fadeInLeft 0.8s ease-out forwards;
        }
        
        .animate-fadeInRight {
          animation: fadeInRight 0.8s ease-out forwards;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.6s ease-out forwards;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-delay-100 { animation-delay: 0.1s; }
        .animate-delay-200 { animation-delay: 0.2s; }
        .animate-delay-300 { animation-delay: 0.3s; }
        .animate-delay-400 { animation-delay: 0.4s; }
        .animate-delay-500 { animation-delay: 0.5s; }
        .animate-delay-600 { animation-delay: 0.6s; }
        
        .opacity-0 { opacity: 0; }
      `}</style>

      {/* Background decorations */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-green-50/30 to-white"></div>
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-green-100/50 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-100/50 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-12 md:mb-16 ${headerVisible ? '' : 'opacity-0'}`}
        >
          <div className={`inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full text-green-700 text-sm font-medium mb-4 ${headerVisible ? 'animate-fadeInUp' : ''}`}>
            <Sparkles className="w-4 h-4" />
            Nouveau : Carnet de Santé Digital
          </div>
          <h2 className={`text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 ${headerVisible ? 'animate-fadeInUp animate-delay-100' : ''}`}>
            Le <span className="text-green-600">carnet de santé</span>
            <br className="hidden sm:block" />
            de votre animal, 100% digital
          </h2>
          <p className={`text-lg md:text-xl text-gray-600 max-w-3xl mx-auto ${headerVisible ? 'animate-fadeInUp animate-delay-200' : ''}`}>
            Centralisez l'historique médical de vos animaux, recevez des rappels de vaccination automatiques 
            et partagez facilement avec tous vos vétérinaires.
          </p>
        </div>

        {/* Main visual card */}
        <div 
          ref={cardRef}
          className={`mb-16 ${cardVisible ? 'animate-scaleIn' : 'opacity-0'}`}
        >
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
            <div className="grid lg:grid-cols-2">
              {/* Left: Visual illustration */}
              <div className={`bg-gradient-to-br from-green-500 to-emerald-600 p-8 md:p-12 text-white relative overflow-hidden ${cardVisible ? 'animate-fadeInLeft animate-delay-200' : ''}`}>
                {/* Decorative circles */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                
                <div className="relative">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm animate-float">
                      <Heart className="w-8 h-8" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">Carnet de Santé</h3>
                      <p className="text-green-100">Animal Digital</p>
                    </div>
                  </div>

                  {/* Mock carnet preview */}
                  <div className={`bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-6 ${cardVisible ? 'animate-fadeInUp animate-delay-300' : ''}`}>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center text-3xl animate-float" style={{animationDelay: '0.5s'}}>
                        🐕
                      </div>
                      <div>
                        <p className="font-bold text-lg">Rex</p>
                        <p className="text-green-100 text-sm">Berger Allemand • 6 ans</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-300" />
                        <span>Vaccins à jour</span>
                        <span className="ml-auto bg-green-400/30 px-2 py-0.5 rounded text-xs">✓ 2/2</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <FileText className="w-4 h-4 text-green-300" />
                        <span>Documents</span>
                        <span className="ml-auto bg-green-400/30 px-2 py-0.5 rounded text-xs">3 fichiers</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Share2 className="w-4 h-4 text-green-300" />
                        <span>Partagé avec</span>
                        <span className="ml-auto bg-green-400/30 px-2 py-0.5 rounded text-xs">1 véto</span>
                      </div>
                    </div>
                  </div>

                  {/* Trust badges */}
                  <div className={`flex flex-wrap gap-3 ${cardVisible ? 'animate-fadeInUp animate-delay-400' : ''}`}>
                    <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-lg text-sm">
                      <Lock className="w-4 h-4" />
                      <span>Sécurisé</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-lg text-sm">
                      <Cloud className="w-4 h-4" />
                      <span>Cloud</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-lg text-sm">
                      <Shield className="w-4 h-4" />
                      <span>RGPD</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: CTA and key features */}
              <div className={`p-8 md:p-12 flex flex-col justify-center ${cardVisible ? 'animate-fadeInRight animate-delay-300' : ''}`}>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Tout l'historique médical
                  <br />
                  <span className="text-green-600">accessible en un instant</span>
                </h3>
                
                <p className="text-gray-600 mb-6">
                  Finies les ordonnances perdues et les rappels oubliés. 
                  Gérez la santé de vos animaux simplement et partagez leur dossier 
                  avec n'importe quel vétérinaire.
                </p>

                <ul className="space-y-3 mb-8">
                  {[
                    "Création gratuite et illimitée",
                    "Multi-animaux supporté",
                    "Rappels vaccination automatiques",
                    "Partage sécurisé avec les vétos"
                  ].map((item, index) => (
                    <li key={index} className={`flex items-center gap-3 text-gray-700 ${cardVisible ? 'animate-fadeInUp' : ''}`} style={{animationDelay: `${0.4 + index * 0.1}s`}}>
                      <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-3 h-3 text-green-600" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button 
                    onClick={handleCTA}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-6 text-lg rounded-xl shadow-lg shadow-green-600/25 transition-all hover:shadow-xl hover:shadow-green-600/30 hover:scale-105"
                    data-testid="cta-create-carnet"
                  >
                    <Heart className="w-5 h-5 mr-2" />
                    Créer le carnet de mon animal
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>

                <p className="text-sm text-gray-500 mt-4 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Gratuit pour les propriétaires • Données sécurisées
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Visual Flow Diagram - Propriétaire → Carnet → Vétérinaire */}
        <div 
          ref={flowRef}
          className={`mb-16 ${flowVisible ? '' : 'opacity-0'}`}
        >
          <h3 className={`text-2xl md:text-3xl font-bold text-center text-gray-900 mb-4 ${flowVisible ? 'animate-fadeInUp' : ''}`}>
            Le dossier médical partagé, simplifié
          </h3>
          <p className={`text-center text-gray-600 mb-10 max-w-2xl mx-auto ${flowVisible ? 'animate-fadeInUp animate-delay-100' : ''}`}>
            Une solution unique qui connecte propriétaires et vétérinaires autour de la santé animale
          </p>
          
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-0">
            {/* Propriétaire */}
            <div className={`flex flex-col items-center ${flowVisible ? 'animate-fadeInLeft' : ''}`}>
              <div className="w-24 h-24 md:w-32 md:h-32 bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl flex items-center justify-center text-5xl md:text-6xl shadow-xl shadow-blue-500/25 hover:scale-105 transition-transform">
                👤
              </div>
              <h4 className="mt-4 font-bold text-gray-900">Propriétaire</h4>
              <p className="text-sm text-gray-500 text-center max-w-[120px]">Crée et enrichit le carnet</p>
            </div>

            {/* Arrow 1 */}
            <div className={`flex items-center px-2 md:px-4 ${flowVisible ? 'animate-fadeInUp animate-delay-200' : ''}`}>
              <div className="hidden md:flex items-center">
                <div className="w-16 h-1 bg-gradient-to-r from-blue-400 to-green-400 rounded-full"></div>
                <ChevronRight className="w-8 h-8 text-green-500 -ml-2" />
              </div>
              <div className="md:hidden">
                <div className="w-1 h-8 bg-gradient-to-b from-blue-400 to-green-400 rounded-full mx-auto"></div>
                <ChevronRight className="w-8 h-8 text-green-500 rotate-90 -mt-2" />
              </div>
            </div>

            {/* Carnet (Central) */}
            <div className={`flex flex-col items-center ${flowVisible ? 'animate-scaleIn animate-delay-300' : ''}`}>
              <div className="relative">
                <div className="w-32 h-32 md:w-40 md:h-40 bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl flex items-center justify-center shadow-2xl shadow-green-500/30 hover:scale-105 transition-transform">
                  <div className="text-center text-white">
                    <Heart className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-1" />
                    <span className="text-xs md:text-sm font-bold">CARNET</span>
                  </div>
                </div>
                {/* Floating badges */}
                <div className="absolute -top-2 -right-2 bg-white rounded-full p-2 shadow-lg animate-float">
                  <Syringe className="w-5 h-5 text-purple-500" />
                </div>
                <div className="absolute -bottom-2 -left-2 bg-white rounded-full p-2 shadow-lg animate-float" style={{animationDelay: '0.5s'}}>
                  <FileText className="w-5 h-5 text-blue-500" />
                </div>
                <div className="absolute top-1/2 -right-4 bg-white rounded-full p-2 shadow-lg animate-float" style={{animationDelay: '1s'}}>
                  <Bell className="w-5 h-5 text-orange-500" />
                </div>
              </div>
              <h4 className="mt-4 font-bold text-gray-900">Carnet Digital</h4>
              <p className="text-sm text-gray-500 text-center max-w-[140px]">Centralise tout l'historique médical</p>
            </div>

            {/* Arrow 2 */}
            <div className={`flex items-center px-2 md:px-4 ${flowVisible ? 'animate-fadeInUp animate-delay-400' : ''}`}>
              <div className="hidden md:flex items-center">
                <div className="w-16 h-1 bg-gradient-to-r from-green-400 to-purple-400 rounded-full"></div>
                <ChevronRight className="w-8 h-8 text-purple-500 -ml-2" />
              </div>
              <div className="md:hidden">
                <div className="w-1 h-8 bg-gradient-to-b from-green-400 to-purple-400 rounded-full mx-auto"></div>
                <ChevronRight className="w-8 h-8 text-purple-500 rotate-90 -mt-2" />
              </div>
            </div>

            {/* Vétérinaire */}
            <div className={`flex flex-col items-center ${flowVisible ? 'animate-fadeInRight animate-delay-500' : ''}`}>
              <div className="w-24 h-24 md:w-32 md:h-32 bg-gradient-to-br from-purple-500 to-purple-600 rounded-3xl flex items-center justify-center text-5xl md:text-6xl shadow-xl shadow-purple-500/25 hover:scale-105 transition-transform">
                👨‍⚕️
              </div>
              <h4 className="mt-4 font-bold text-gray-900">Vétérinaire</h4>
              <p className="text-sm text-gray-500 text-center max-w-[120px]">Consulte et enrichit le dossier</p>
            </div>
          </div>
        </div>

        {/* Feature Showcase / Carousel */}
        <div 
          ref={showcaseRef}
          className={`mb-16 ${showcaseVisible ? '' : 'opacity-0'}`}
        >
          <h3 className={`text-2xl md:text-3xl font-bold text-center text-gray-900 mb-4 ${showcaseVisible ? 'animate-fadeInUp' : ''}`}>
            Découvrez les fonctionnalités
          </h3>
          <p className={`text-center text-gray-600 mb-10 ${showcaseVisible ? 'animate-fadeInUp animate-delay-100' : ''}`}>
            Un outil complet pour gérer la santé de vos animaux
          </p>

          <div className={`bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 ${showcaseVisible ? 'animate-scaleIn animate-delay-200' : ''}`}>
            <div className="grid lg:grid-cols-2">
              {/* Left: Feature selector */}
              <div className="p-6 md:p-8 bg-gray-50 border-r border-gray-100">
                <div className="space-y-3">
                  {featureShowcase.map((feature, index) => (
                    <button
                      key={feature.id}
                      onClick={() => setActiveFeature(index)}
                      className={`w-full p-4 rounded-2xl text-left transition-all ${
                        activeFeature === index
                          ? 'bg-white shadow-lg scale-[1.02]'
                          : 'hover:bg-white/50'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-2xl shadow-lg`}>
                          {feature.icon}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{feature.title}</h4>
                          <p className="text-sm text-gray-500 line-clamp-1">{feature.description}</p>
                        </div>
                        {activeFeature === index && (
                          <ChevronRight className="w-5 h-5 text-green-500" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Right: Feature preview */}
              <div className="p-6 md:p-8 flex flex-col">
                <div className={`flex-1 bg-gradient-to-br ${featureShowcase[activeFeature].color} rounded-2xl p-6 md:p-8 text-white relative overflow-hidden transition-all duration-500`}>
                  {/* Decorative */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                  
                  <div className="relative">
                    <div className="text-6xl mb-4">{featureShowcase[activeFeature].icon}</div>
                    <h4 className="text-2xl font-bold mb-2">{featureShowcase[activeFeature].title}</h4>
                    <p className="text-white/90 mb-6">{featureShowcase[activeFeature].description}</p>
                    
                    <div className="grid grid-cols-2 gap-3">
                      {featureShowcase[activeFeature].features.map((feat, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white/20 rounded-lg px-3 py-2 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          {feat}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Progress dots */}
                <div className="flex justify-center gap-2 mt-4">
                  {featureShowcase.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveFeature(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        activeFeature === index
                          ? 'w-8 bg-green-500'
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* How it works - Steps */}
        <div 
          ref={stepsRef}
          className={`mb-16 ${stepsVisible ? '' : 'opacity-0'}`}
        >
          <h3 className={`text-2xl md:text-3xl font-bold text-center text-gray-900 mb-10 ${stepsVisible ? 'animate-fadeInUp' : ''}`}>
            Comment ça marche ?
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {steps.map((step, index) => (
              <div 
                key={index} 
                className={`text-center group ${stepsVisible ? 'animate-fadeInUp' : ''}`}
                style={{animationDelay: `${0.1 + index * 0.15}s`}}
              >
                <div className="relative mb-4">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-lg group-hover:scale-110 transition-transform group-hover:shadow-xl">
                    {step.number}
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 left-full w-full h-0.5 bg-gradient-to-r from-green-300 to-transparent -translate-y-1/2"></div>
                  )}
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">{step.title}</h4>
                <p className="text-sm text-gray-500">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits Grid */}
        <div 
          ref={benefitsRef}
          className={`grid md:grid-cols-2 gap-8 mb-16 ${benefitsVisible ? '' : 'opacity-0'}`}
        >
          {/* Owner benefits */}
          <div className={`bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100 ${benefitsVisible ? 'animate-fadeInLeft' : ''}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-100 rounded-xl">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Pour les propriétaires</h3>
            </div>
            
            <div className="space-y-4">
              {ownerBenefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div 
                    key={index} 
                    className={`flex items-start gap-4 group ${benefitsVisible ? 'animate-fadeInUp' : ''}`}
                    style={{animationDelay: `${0.2 + index * 0.1}s`}}
                  >
                    <div className="p-2 bg-gray-50 rounded-lg group-hover:bg-green-50 transition-colors">
                      <Icon className="w-5 h-5 text-gray-600 group-hover:text-green-600 transition-colors" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{benefit.title}</h4>
                      <p className="text-sm text-gray-500">{benefit.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Vet benefits */}
          <div className={`bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100 ${benefitsVisible ? 'animate-fadeInRight animate-delay-200' : ''}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-100 rounded-xl">
                <Syringe className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Pour les vétérinaires</h3>
            </div>
            
            <div className="space-y-4">
              {vetBenefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div 
                    key={index} 
                    className={`flex items-start gap-4 group ${benefitsVisible ? 'animate-fadeInUp' : ''}`}
                    style={{animationDelay: `${0.3 + index * 0.1}s`}}
                  >
                    <div className="p-2 bg-gray-50 rounded-lg group-hover:bg-purple-50 transition-colors">
                      <Icon className="w-5 h-5 text-gray-600 group-hover:text-purple-600 transition-colors" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{benefit.title}</h4>
                      <p className="text-sm text-gray-500">{benefit.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-100">
              <Button 
                onClick={() => navigate('/espace-veterinaire')}
                variant="outline"
                className="w-full border-purple-200 text-purple-600 hover:bg-purple-50 hover:scale-102 transition-all"
              >
                Espace vétérinaire
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>

        {/* Testimonials Section */}
        <div 
          ref={testimonialsRef}
          className={`mb-16 ${testimonialsVisible ? '' : 'opacity-0'}`}
        >
          <h3 className={`text-2xl md:text-3xl font-bold text-center text-gray-900 mb-4 ${testimonialsVisible ? 'animate-fadeInUp' : ''}`}>
            Ils nous font confiance
          </h3>
          <p className={`text-center text-gray-600 mb-10 max-w-2xl mx-auto ${testimonialsVisible ? 'animate-fadeInUp animate-delay-100' : ''}`}>
            Découvrez ce que pensent nos utilisateurs du carnet de santé digital
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className={`bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all hover:-translate-y-1 ${testimonialsVisible ? 'animate-fadeInUp' : ''}`}
                style={{animationDelay: `${0.2 + index * 0.15}s`}}
              >
                {/* Rating stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>

                {/* Quote */}
                <p className="text-gray-700 mb-6 leading-relaxed italic">
                  "{testimonial.text}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center text-2xl">
                    {testimonial.avatar}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      {testimonial.role}
                      {testimonial.animalName && (
                        <span className="ml-1">
                          • {testimonial.animalEmoji} {testimonial.animalName}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Trust badge */}
          <div className={`flex justify-center mt-8 ${testimonialsVisible ? 'animate-fadeInUp animate-delay-500' : ''}`}>
            <div className="inline-flex items-center gap-3 bg-green-50 px-6 py-3 rounded-full">
              <div className="flex -space-x-2">
                <div className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center text-sm border-2 border-white">🐕</div>
                <div className="w-8 h-8 bg-blue-200 rounded-full flex items-center justify-center text-sm border-2 border-white">🐱</div>
                <div className="w-8 h-8 bg-purple-200 rounded-full flex items-center justify-center text-sm border-2 border-white">🐰</div>
              </div>
              <span className="text-green-700 font-medium">+1 000 animaux enregistrés</span>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div 
          ref={ctaRef}
          className={`text-center bg-gradient-to-r from-green-600 to-emerald-600 rounded-3xl p-8 md:p-12 text-white relative overflow-hidden ${ctaVisible ? 'animate-scaleIn' : 'opacity-0'}`}
        >
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzBoLTZWMGg2djMwem0tNiAwSDI0VjBoNnYzMHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-20"></div>
          
          <div className="relative">
            <h3 className={`text-2xl md:text-3xl font-bold mb-4 ${ctaVisible ? 'animate-fadeInUp' : ''}`}>
              Prêt à simplifier le suivi santé de votre animal ?
            </h3>
            <p className={`text-green-100 mb-8 max-w-2xl mx-auto ${ctaVisible ? 'animate-fadeInUp animate-delay-100' : ''}`}>
              Rejoignez les milliers de propriétaires qui utilisent vetoRDV pour gérer 
              la santé de leurs compagnons. C'est gratuit et ça prend 2 minutes.
            </p>
            
            <div className={`flex flex-col sm:flex-row gap-4 justify-center ${ctaVisible ? 'animate-fadeInUp animate-delay-200' : ''}`}>
              <Button 
                onClick={handleCTA}
                className="bg-white text-green-600 hover:bg-green-50 px-8 py-6 text-lg rounded-xl shadow-lg hover:scale-105 transition-all"
                data-testid="cta-bottom-create-carnet"
              >
                <Heart className="w-5 h-5 mr-2" />
                Créer mon carnet gratuit
              </Button>
              <Button 
                onClick={() => navigate('/recherche')}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-xl hover:scale-105 transition-all"
              >
                Trouver un vétérinaire
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CarnetSanteSection;
